/* eslint-disable indent */
/* eslint-disable max-lines-per-function */
/* eslint-disable no-unused-vars */
import { useContext, useEffect, useState } from "react";
import ListCarriers from "../tables/ListCarriers";
import { UserContext } from "../../contexts/userContext";
import { Grid } from "@mui/material";
import {
  OWN_FLEET_SECTION_CARRIERS_BY_ENTITY,
  CONFIG_SUCCESS_MESSAGE,
  ECOMMERCE,
  ERROR,
  MEDIUM,
  MERCHANT,
  SUCCESS,
  SUCCESS_ALERT_DURATION,
} from "../../utils/constants";
import { getCarriers } from "../../requests/carrierRequests";
import {
  ICarrier,
  ICarrierChanged,
  ICarriersChanges,
  IConfigurationRules,
  IModalsState,
  IOwnLogisticsCredentials,
  IOwnLogisticsMatrix,
} from "../../interfaces/configuration";
import Loader from "../Loader";
import ConfirmChangesButtons from "./ConfirmChangesButtons";
import { IAlert } from "../../interfaces/alert";
import GenericAlert from "../GenericAlert";
import SearchCarriers from "./SearchCarriers";
import {
  getFormDataFromCarriersInfo,
  getOwnFleetMatricesToShow,
} from "../../utils/credentials";
import CredentialsModal from "./CredentialsModal";
import { ConfigurationContext } from "../../contexts/configurationContext";
import { OwnFleetSettings } from "./OwnFleetSettings";
import { getCSVUploadStatus } from "../../utils/configuration";

const parseCarriers = (
  carriers: ICarrier[],
  rules: IConfigurationRules
): ICarrier[] =>
  carriers.map(({ disabled, credentials, ...rest }: ICarrier) => ({
    ...rest,
    credentials,
    disabled: disabled || (rules.canFulfillCredentials && !credentials),
  }));

const CarrierPartnersView = ({
  partnersCarriersChanges,
  handleGetCarriers,
  filteredCarriersPartners,
  handleCarriersChanges,
  handleSubmitChanges,
  isSubmitting,
  setOpenModal,
}: {
  partnersCarriersChanges: ICarriersChanges;
  handleGetCarriers: () => void;
  filteredCarriersPartners: ICarrier[];
  handleCarriersChanges: (carrier: ICarrierChanged) => void;
  handleSubmitChanges: () => void;
  isSubmitting: boolean;
  setOpenModal: ({ type, open }: { type: string; open: boolean }) => void;
}) => {
  const {
    currentUser: { entity },
  } = useContext(UserContext);
  const { configurationRules } = useContext(ConfigurationContext);
  const hasChanges = Object.keys(partnersCarriersChanges).length > 0;
  return (
    <Grid container flexDirection="column" className="m-t-30">
      <Grid item md={5} lg={3}>
        <SearchCarriers
          handleGetCarriers={handleGetCarriers}
          disabled={filteredCarriersPartners.length === 0}
        />
      </Grid>
      {filteredCarriersPartners.length > 0 && (
        <>
          <Grid item xs={12} xl={7}>
            <ListCarriers
              carriers={parseCarriers(
                filteredCarriersPartners,
                configurationRules
              )}
              carriersChanges={partnersCarriersChanges}
              handleCarriersChanges={handleCarriersChanges}
              setOpenModal={setOpenModal}
              entity={entity}
            />
          </Grid>
          {hasChanges && (
            <Grid item md={12} lg={12} xl={7}>
              <ConfirmChangesButtons
                handleConfirm={handleSubmitChanges}
                handleCancel={() =>
                  setOpenModal({ type: "confirmCancel", open: true })
                }
                disableConfirmButton={isSubmitting}
                confirmButtonText="Guardar Configuración"
                loadingConfirmButton={isSubmitting}
              />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};
const OwnFleetView = ({
  ownFleetCarriersMatrixChanges,
  ownFleetCarrier,
  handleCarriersChanges,
  handleSubmitChanges,
  isSubmitting,
  setOpenModal,
}: {
  ownFleetCarriersMatrixChanges: ICarriersChanges;
  ownFleetCarrier: any;
  handleCarriersChanges: (carrier: ICarrierChanged) => void;
  handleSubmitChanges: () => void;
  isSubmitting: boolean;
  setOpenModal: ({ type, open }: { type: string; open: boolean }) => void;
}) => {
  const hasChanges = Object.keys(ownFleetCarriersMatrixChanges).length > 0;
  const [hasMatrixFileError, setHasMatrixFileError] = useState(false);
  const changedCarrier = ownFleetCarriersMatrixChanges[ownFleetCarrier.name];

  const changedCredentials =
    changedCarrier?.credentials as IOwnLogisticsCredentials;

  const matricesToShow = getOwnFleetMatricesToShow(
    ownFleetCarrier?.credentials?.matrices || [],
  );
  const activeMatrix =
    ownFleetCarrier?.credentials?.matrices?.find(
      (m: IOwnLogisticsMatrix) => m.active
    ) ??
    matricesToShow?.[0];
  const activeMatrixGeoType = activeMatrix?.geographic_type || "ZIP_CODE";

  const currentGeoType =
    changedCredentials?.geographic_type ||
    ownFleetCarrier.credentials?.geographic_type ||
    "ZIP_CODE";

  const isGeoTypeMismatch = currentGeoType !== activeMatrixGeoType;

  const hasNewMatrix = !!changedCredentials?.matrix;

  const csvStatus = getCSVUploadStatus(matricesToShow);
  const isProcessing = csvStatus === "in-process";

  const isBlockingSave = (isGeoTypeMismatch && !hasNewMatrix) || isProcessing;

  const effectiveOwnFleetCarrier = changedCarrier
    ? {
        ...ownFleetCarrier,
        ...changedCarrier,
        credentials: {
          ...ownFleetCarrier.credentials,
          ...changedCarrier.credentials,
        },
      }
    : ownFleetCarrier;

  return (
    <Grid container flexDirection="column" className="m-t-30">
      <Grid item xs={12} xl={7}>
        <OwnFleetSettings
          ownFleetCarrier={effectiveOwnFleetCarrier}
          handleCarriersChanges={handleCarriersChanges}
          onMatrixValidationChange={setHasMatrixFileError}
        />
      </Grid>
      <Grid item md={12} lg={12} xl={7}>
        {hasChanges && (
          <ConfirmChangesButtons
            handleConfirm={handleSubmitChanges}
            handleCancel={() =>
              setOpenModal({ type: "confirmCancel", open: true })
            }
            disableConfirmButton={isSubmitting || isBlockingSave || hasMatrixFileError}
            confirmButtonText="Guardar Configuración"
            loadingConfirmButton={isSubmitting}
          />
        )}
      </Grid>
    </Grid>
  );
};

const Carriers = () => {
  const {
    currentUser: { entity },
    selectedMarketplace,
    refreshUserData,
  } = useContext(UserContext);
  const { availableCarriers, loading: loadingFromContext } =
    useContext(ConfigurationContext);

  const [carriers, setCarriers] = useState(availableCarriers);
  const [partnersCarriersChanges, setPartnersCarriersChanges] = useState(
    {} as ICarriersChanges
  );
  const [ownFleetCarriersMatrixChanges, setOwnFleetCarriersMatrixChanges] =
    useState({} as ICarriersChanges);
  const [alert, setAlert] = useState({} as IAlert);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState({} as IModalsState);

  const { partnersCarriers, ownCarriers } = carriers.reduce(
    (acc, carrier) => {
      if (OWN_FLEET_SECTION_CARRIERS_BY_ENTITY[entity]?.includes(carrier.name)) {
        acc.ownCarriers.push(carrier);
      } else {
        acc.partnersCarriers.push(carrier);
      }
      return acc;
    },
    { partnersCarriers: [] as ICarrier[], ownCarriers: [] as ICarrier[] }
  );

  const isLoading = loading || loadingFromContext;

  const clearStates = () => {
    setAlert({} as IAlert);
    setCarriers([]);
    setOwnFleetCarriersMatrixChanges({} as ICarriersChanges);
    setPartnersCarriersChanges({} as ICarriersChanges);
    setIsSubmitting(false);
  };

  const handlePartnersCarriersChanges = (carrierToUpdate: ICarrierChanged) => {
    setIsSubmitting(false);
    setPartnersCarriersChanges({
      ...partnersCarriersChanges,
      [carrierToUpdate.name]: { ...carrierToUpdate },
    });
  };
  const handleOwnFleetCarriersChanges = (carrierToUpdate: ICarrierChanged) => {
    setOwnFleetCarriersMatrixChanges({
      ...ownFleetCarriersMatrixChanges,
      [carrierToUpdate.name]: { ...carrierToUpdate },
    });
  };

  // Note: [handlePartnersAndOwnCarriers] manage own_fleet and oca, rapiboy, andreani too.
  const handleGetPartnersAndOwnCarriers = async (searchValue = "") => {
    clearStates();
    setLoading(true);
    const urlQueryParams = searchValue
      ? `${entity === MERCHANT ? "&" : "?"}carrierName=${searchValue}`
      : searchValue;
    try {
      const fetchedCarriers = await getCarriers({
        entity,
        selectedMarketplace,
        urlQueryParams,
        fetchCredentials: true,
      });
      setCarriers(fetchedCarriers);
    } catch (error: any) {
      setAlert({
        typeOfAlert: ERROR,
        message: error,
        showAlert: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitChanges = async () => {
    setAlert({} as IAlert);
    setIsSubmitting(true);
    try {
      await getFormDataFromCarriersInfo(
        { ...partnersCarriersChanges, ...ownFleetCarriersMatrixChanges },
        entity,
        selectedMarketplace
      );
      refreshUserData();
      setAlert({
        typeOfAlert: SUCCESS,
        message: CONFIG_SUCCESS_MESSAGE,
        showAlert: true,
      });
      setTimeout(handleGetPartnersAndOwnCarriers, SUCCESS_ALERT_DURATION);
    } catch (error: any) {
      setAlert({
        typeOfAlert: ERROR,
        message: error,
        showAlert: true,
      });
    }
  };

  useEffect(() => {
    setAlert(
      !isLoading && carriers.length === 0
        ? {
            typeOfAlert: ERROR,
            message: "No se encontraron operadores logísticos.",
            showAlert: true,
          }
        : ({} as IAlert)
    );
  }, [carriers, loading, loadingFromContext]);

  useEffect(() => {
    setCarriers(availableCarriers);
  }, [availableCarriers]);

  return (
    <>
      {alert.showAlert && <GenericAlert alert={alert} />}
      {openModal.open && (
        <CredentialsModal
          carriers={partnersCarriers} //OCA, RAPIBOY, ANDREANI .. not OWN_FLEET
          carriersChanges={partnersCarriersChanges}
          openModal={openModal}
          setOpenModal={setOpenModal}
          handleGetCarriers={handleGetPartnersAndOwnCarriers}
          handleCarriersChanges={handlePartnersCarriersChanges}
          handleSubmitChanges={handleSubmitChanges}
          entity={entity}
        />
      )}
      <CarrierPartnersView
        partnersCarriersChanges={partnersCarriersChanges}
        handleGetCarriers={handleGetPartnersAndOwnCarriers}
        filteredCarriersPartners={partnersCarriers}
        handleCarriersChanges={handlePartnersCarriersChanges}
        handleSubmitChanges={handleSubmitChanges}
        isSubmitting={isSubmitting}
        setOpenModal={setOpenModal}
      />
      {ownCarriers.length > 0 && (entity === MERCHANT || entity === ECOMMERCE) && (
        <OwnFleetView
          ownFleetCarriersMatrixChanges={ownFleetCarriersMatrixChanges}
          ownFleetCarrier={ownCarriers[0]}
          handleCarriersChanges={handleOwnFleetCarriersChanges}
          handleSubmitChanges={handleSubmitChanges}
          isSubmitting={isSubmitting}
          setOpenModal={setOpenModal}
        />
      )}

      {isLoading && !alert.showAlert && <Loader size={MEDIUM} />}
    </>
  );
};

export default Carriers;
